import React, { useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { scrollToSection } from '../lib/scroll';

const Hero = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const scrollToConsultation = () => {
    scrollToSection('consultation', -100);
  };

  // Line-by-line reveal animation variants
  const lineVariants = {
    hidden: { opacity: 0, y: 50, clipPath: 'inset(0 0 100% 0)' },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      clipPath: 'inset(0 0 0% 0)',
      transition: {
        duration: 1,
        delay: 0.1 + i * 0.15,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  const sublineVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.8 + i * 0.1,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0f1419] via-[#1a2332] to-[#162030]"
    >
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(201, 169, 97, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(201, 169, 97, 0.1) 1px, transparent 1px)',
            backgroundSize: '100px 100px',
          }}
        />
      </div>

      {/* Parallax orbs */}
      <motion.div
        style={{ y }}
        className="absolute top-20 right-20 w-96 h-96 bg-[#c9a961]/10 rounded-full blur-3xl"
      />
      <motion.div
        style={{ y: useTransform(scrollY, [0, 500], [0, -100]) }}
        className="absolute bottom-20 left-20 w-72 h-72 bg-[#c9a961]/5 rounded-full blur-3xl"
      />

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-32 text-center"
      >
        {/* Main headline with masked reveal */}
        <div className="mb-8 overflow-hidden">
          <motion.h1
            custom={0}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={lineVariants}
            className="font-serif text-5xl md:text-7xl lg:text-8xl font-light text-white leading-tight mb-2"
          >
            International Wealth
          </motion.h1>
          <motion.h1
            custom={1}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={lineVariants}
            className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#c9a961] leading-tight"
          >
            Protection & Business Strategy Expert
          </motion.h1>
        </div>

        {/* Subheadline */}
        <motion.div
          custom={0}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={sublineVariants}
          className="max-w-3xl mx-auto mb-12"
        >
          <p className="text-lg md:text-xl lg:text-2xl text-gray-300 font-light leading-relaxed">
            Sophisticated drafting and legal engineering at institutional levels
            <br className="hidden md:block" />
            for wealth protection, banking strategy, and cross-border solutions.
          </p>
        </motion.div>

        {/* Credibility markers */}
        <motion.div
          custom={1}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={sublineVariants}
          className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mb-12 flex-wrap"
        >
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-serif text-[#c9a961] mb-2">Institutional</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Drafting & Legal Engineering</div>
          </div>
          <div className="hidden md:block w-px h-12 bg-[#c9a961]/30" />
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-serif text-[#c9a961] mb-2">63+</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Jurisdictions</div>
          </div>
          <div className="hidden md:block w-px h-12 bg-[#c9a961]/30" />
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-serif text-[#c9a961] mb-2">30+</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Years Experience</div>
          </div>
          <div className="hidden md:block w-px h-12 bg-[#c9a961]/30" />
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-serif text-[#c9a961] mb-2">$7T+</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Advisory Volume</div>
          </div>
          <div className="hidden md:block w-px h-12 bg-[#c9a961]/30" />
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-serif text-[#c9a961] mb-2">Global</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Reach</div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          custom={2}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={sublineVariants}
          className="flex flex-col sm:flex-row gap-4 items-center justify-center"
        >
          <Button
            onClick={scrollToConsultation}
            data-testid="hero-consultation-btn"
            className="group bg-[#c9a961] hover:bg-[#b89851] text-[#1a2332] font-semibold px-10 py-7 text-lg rounded-none transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#c9a961]/30 flex items-center gap-3"
          >
            Request Private Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1,
            delay: 2,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-px h-16 bg-gradient-to-b from-[#c9a961] to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
